"use client";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import Link from "~/components/Link";
import { authService } from "~/connectors/auth.connector";

import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { useSession } from "next-auth/react";
import Image from "next/image";
import { SITE_CONFIG } from "~/lib/constants";
import { storeFrontMenuEntries } from "./storeFrontMenuEntries";

import Box from "@mui/material/Box";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItemIcon from "@mui/material/ListItemIcon";

interface StoreFrontDrawerProps {
  open: boolean;
  onClose: () => void;
}

export default function StoreFrontDrawer({
  open,
  onClose,
}: StoreFrontDrawerProps) {
  const { data: session } = useSession();
  const user = session?.user;

  const handleLogout = async () => {
    await authService.logout("/");
    onClose();
  };

  return (
    <Drawer
      variant="temporary"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        display: { xs: "block", md: "none" },
        "& .MuiDrawer-paper": {
          boxSizing: "border-box",
          width: 250,
        },
      }}
    >
      <Box sx={{ width: 250, pt: 2 }}>
        <ListItem disablePadding sx={{ mb: 1 }}>
          <ListItemButton LinkComponent={Link} href="/" onClick={onClose}>
            <ListItemIcon>
              <Image
                src="/logo.png"
                alt="Cantecelulmeu Logo"
                width={32}
                height={32}
                style={{ marginRight: 8 }}
              />
            </ListItemIcon>
            <ListItemText primary={SITE_CONFIG.name} />
          </ListItemButton>
        </ListItem>

        <List>
          {storeFrontMenuEntries.map((item) => (
            <ListItem key={item.label} disablePadding>
              <ListItemButton
                LinkComponent={Link}
                href={item.href}
                onClick={onClose}
              >
                <ListItemIcon color="inherit">
                  <item.icon color="inherit" />
                </ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            </ListItem>
          ))}

          {user && (
            <Box
              sx={{ my: 2, mx: 2, borderBottom: 1, borderColor: "divider" }}
            />
          )}

          {user && (
            <ListItem disablePadding>
              <ListItemButton
                LinkComponent={Link}
                href="/dashboard/comenzi"
                onClick={onClose}
              >
                <ListItemIcon>
                  <ReceiptLongIcon />
                </ListItemIcon>
                <ListItemText primary="Comenzile Mele" />
              </ListItemButton>
            </ListItem>
          )}

          {user ? (
            <>
              <ListItem disablePadding>
                <ListItemButton
                  LinkComponent={Link}
                  href="/dashboard/profil"
                  onClick={onClose}
                >
                  <ListItemIcon>
                    <AccountCircleIcon />
                  </ListItemIcon>
                  <ListItemText primary="Contul Meu" />
                </ListItemButton>
              </ListItem>
              {user.role === "admin" && (
                <ListItem disablePadding>
                  <ListItemButton
                    LinkComponent={Link}
                    href="/admin"
                    onClick={onClose}
                  >
                    <ListItemIcon>
                      <AdminPanelSettingsIcon color="error" />
                    </ListItemIcon>
                    <ListItemText primary="Admin" />
                  </ListItemButton>
                </ListItem>
              )}
              <Box
                sx={{ my: 2, mx: 2, borderBottom: 1, borderColor: "divider" }}
              />
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    handleLogout();
                  }}
                >
                  <ListItemIcon>
                    <LogoutIcon />
                  </ListItemIcon>
                  <ListItemText primary="Deconectare" />
                </ListItemButton>
              </ListItem>
            </>
          ) : (
            <>
              <Box
                sx={{ my: 2, mx: 2, borderBottom: 1, borderColor: "divider" }}
              />
              <ListItem disablePadding>
                <ListItemButton
                  LinkComponent={Link}
                  href="/login"
                  onClick={onClose}
                >
                  <ListItemIcon>
                    <LoginIcon />
                  </ListItemIcon>
                  <ListItemText primary="Conectare" />
                </ListItemButton>
              </ListItem>
            </>
          )}
        </List>
      </Box>
    </Drawer>
  );
}
